import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Handshake, Plus, Pencil, Trash2, Mail, Phone, Building2 } from "lucide-react";

const TYPES = [
    { code: "institution", label: "Institucion" },
    { code: "company", label: "Kompani" },
    { code: "ngo", label: "OJQ" },
    { code: "university", label: "Universitet" },
    { code: "other", label: "Tjetër" },
];

const STATUS_TONES = {
    active: "bg-sage/15 text-sage border-sage/30",
    paused: "bg-sand/30 text-amber-800 border-sand/50",
    ended: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function DrejtoreshaCollaborations() {
    const { user } = useAuth();
    const backHome = user?.role === "administrata" ? "/administrata" : user?.role === "super_admin" ? "/admin" : "/drejtoresha";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try { const r = await api.get("/drejtoresha/collaborations"); setItems(r.data || []); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);

    const del = async (id) => {
        if (!window.confirm("Fshi këtë bashkëpunim?")) return;
        await api.delete(`/drejtoresha/collaboration/${id}`);
        refresh();
    };

    return (
        <Layout>
            <Link to={backHome} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4" data-testid="collab-back">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>

            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Bashkëpunime</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Institucione & Kompani</h1>
                    <p className="mt-1 text-slate-600 text-sm">Lista e organizatave që bashkëpunojnë me shkollën.</p>
                </div>
                <CollabDialog onDone={refresh} />
            </div>

            {loading ? <div className="text-sm text-slate-500">Duke ngarkuar…</div> :
             items.length === 0 ? <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500">Asnjë bashkëpunim i regjistruar ende.</div> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collab-list">
                    {items.map((c) => (
                        <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`collab-card-${c.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-10 h-10 rounded-lg bg-sage/15 text-sage flex items-center justify-center shrink-0">
                                        <Building2 className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-bold text-slate-900 truncate">{c.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                                            {(TYPES.find((t) => t.code === c.type) || {}).label}
                                        </div>
                                    </div>
                                </div>
                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase ${STATUS_TONES[c.status] || STATUS_TONES.active}`}>
                                    {c.status === "paused" ? "I ndaluar" : c.status === "ended" ? "I mbaruar" : "Aktiv"}
                                </span>
                            </div>
                            {c.contact_person && <div className="text-xs text-slate-700 mb-1">{c.contact_person}</div>}
                            <div className="flex flex-wrap gap-2 text-[11px] mb-2">
                                {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 text-seablue hover:underline"><Phone className="w-3 h-3" /> {c.phone}</a>}
                                {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 text-seablue hover:underline"><Mail className="w-3 h-3" /> {c.email}</a>}
                            </div>
                            {(c.start_date || c.end_date) && (
                                <div className="text-[11px] text-slate-500 mb-2">
                                    {c.start_date || "—"} → {c.end_date || "…"}
                                </div>
                            )}
                            {c.note && <div className="text-[11px] text-slate-600 italic bg-bone/40 p-2 rounded mb-2">"{c.note}"</div>}
                            <div className="flex items-center gap-2">
                                <CollabDialog initial={c} onDone={refresh} trigger={
                                    <button className="text-xs text-slate-600 inline-flex items-center gap-1 hover:text-oxford" data-testid={`collab-edit-${c.id}`}>
                                        <Pencil className="w-3 h-3" /> Edito
                                    </button>
                                } />
                                <button onClick={() => del(c.id)} className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1 hover:text-terracotta" data-testid={`collab-delete-${c.id}`}>
                                    <Trash2 className="w-3 h-3" /> Fshi
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Layout>
    );
}

const CollabDialog = ({ onDone, initial, trigger }) => {
    const [open, setOpen] = useState(false);
    const blank = { name: "", type: "institution", contact_person: "", email: "", phone: "", note: "", start_date: "", end_date: "", status: "active" };
    const [form, setForm] = useState(initial || blank);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => { if (initial) setForm(initial); }, [initial]);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const payload = { ...form };
            ["start_date", "end_date"].forEach((k) => { if (!payload[k]) delete payload[k]; });
            if (initial?.id) await api.patch(`/drejtoresha/collaboration/${initial.id}`, payload);
            else await api.post("/drejtoresha/collaboration", payload);
            setOpen(false);
            if (!initial) setForm(blank);
            onDone && onDone();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setErr(""); }}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button className="bg-oxford hover:bg-oxfordhover text-white gap-1.5" data-testid="collab-add-btn">
                        <Plus className="w-4 h-4" /> Shto Bashkëpunim
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">{initial ? "Edito" : "Shto"} Bashkëpunim</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <Input placeholder="Emri (p.sh. IBM Kosovë)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="collab-name" />
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="collab-type">
                        {TYPES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                    </select>
                    <Input placeholder="Personi i kontaktit" value={form.contact_person || ""} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="collab-contact" />
                    <Input type="email" placeholder="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="collab-email" />
                    <Input placeholder="Telefon" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="collab-phone" />
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Fillimi</label>
                            <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="collab-start" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Fundi</label>
                            <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="collab-end" />
                        </div>
                    </div>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="collab-status">
                        <option value="active">Aktiv</option>
                        <option value="paused">I ndaluar</option>
                        <option value="ended">I mbaruar</option>
                    </select>
                    <textarea value={form.note || ""} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Shënim (opsional)…"
                        rows={2} className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="collab-note" />
                    {err && <div className="text-xs text-terracotta">{err}</div>}
                    <DialogFooter>
                        <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="collab-submit">
                            {busy ? "Duke ruajtur…" : "Ruaj"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
